// ═══════════════════════════════════════════════════════════════
// vaultZkApi.js — Client /api/vault/zk/* (Session G, V2 zero-knowledge)
// ═══════════════════════════════════════════════════════════════
//
// Mirror de vaultApi.js pour les endpoints zero-knowledge. Aucun
// payload ne contient de passphrase ou de DEK en clair — uniquement
// des blobs déjà chiffrés par le navigateur (vaultZkCrypto.js).

const getToken = () => localStorage.getItem('oracle_token');

export class VaultZkApiError extends Error {
    constructor(status, code, message, detail) {
        super(message || code || `HTTP ${status}`);
        this.name = 'VaultZkApiError';
        this.status = status;
        this.code = code;
        this.detail = detail;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
                'Cache-Control': 'no-store',
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new VaultZkApiError(
                res.status,
                data.error,
                data.message || data.error || `HTTP ${res.status}`,
                data
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

// ─── Challenge / lecture état ──────────────────────────────────

/**
 * GET /api/vault/zk/challenge — retourne metadata ZK pour déverrouillage local.
 * @returns {{
 *   vault_zk_enabled: boolean,
 *   kdf?, iter?, salt?, wrapped_dek?, test_blob?,
 *   recovery_salt?, recovery_blob?,
 *   totp_enabled?, totp_secret_blob?,
 *   activated_at?, vault_locked_until?, vault_burned_at?,
 *   failed_attempts?, burn_threshold?, auto_burn?
 * }}
 */
export const getChallenge = () => request('/api/vault/zk/challenge');

// ─── Activation / désactivation / rotation / récupération ──────

export const activateZk = (payload) =>
    request('/api/vault/zk/activate', { method: 'POST', body: payload });

export const deactivateZk = () =>
    request('/api/vault/zk/deactivate', { method: 'POST' });

export const rotateZk = (payload) =>
    request('/api/vault/zk/rotate', { method: 'POST', body: payload });

export const recoverZk = (payload) =>
    request('/api/vault/zk/recover', { method: 'POST', body: payload });

// ─── Audit déverrouillage ──────────────────────────────────────

/**
 * POST /api/vault/zk/unlock-confirm — déclare succès/échec.
 * Le serveur gère failed_attempts, lock 24h, auto-burn éventuel.
 */
export const confirmUnlock = (success, options = {}) =>
    request('/api/vault/zk/unlock-confirm', {
        method: 'POST',
        body: { success, totp_used: !!options.totp_used },
    });

// ─── 2FA TOTP ──────────────────────────────────────────────────

export const totpSetup = (totp_secret_blob) =>
    request('/api/vault/zk/totp/setup', {
        method: 'POST',
        body: { totp_secret_blob },
    });

export const totpDisable = () =>
    request('/api/vault/zk/totp/disable', { method: 'POST' });

// ─── Migration item V1 → V2 ────────────────────────────────────

/**
 * POST /api/vault/zk/items/:id/migrate
 * @param {number} itemId
 * @param {{ ciphertext: string, iv: string, mode: 'transit'|'strict' }} body
 */
export const migrateItem = (itemId, body) =>
    request(`/api/vault/zk/items/${itemId}/migrate`, { method: 'POST', body });
